<script setup>
// Nexus IA → Uso do WhatsApp (custom/app/controllers/api/v1/accounts/neuxs/whatsapp_uso_controller.rb).
// Por onde saíram as mensagens de cada caixa (aparelho, API oficial, modelos,
// celular), o custo estimado da oficial, a cota grátis do mês e as quedas do
// aparelho. O gráfico é CSS puro: uma coluna empilhada por dia.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { createNexusApi, erroDaApi } from './api';
import messages from './i18n';

const props = defineProps({
  accountId: { type: [Number, String], required: true },
});

const { t } = useI18n({ messages });
const api = computed(() => createNexusApi(props.accountId));

const TIPOS = ['aparelho', 'resposta_api', 'modelos', 'celular'];
// As classes ficam no .vue: o Tailwind não lê os .js desta pasta.
const COR = {
  aparelho: 'bg-n-teal-9',
  resposta_api: 'bg-n-amber-9',
  modelos: 'bg-n-blue-9',
  celular: 'bg-n-slate-8',
};
const ROTULO = {
  aparelho: 'APARELHO',
  resposta_api: 'RESPOSTA_API',
  modelos: 'MODELOS',
  celular: 'CELULAR',
};
const COR_DO_ESTADO = {
  conectada: 'text-n-teal-11',
  desconectada: 'text-n-amber-11',
  substituida: 'text-n-amber-11',
  deslogada: 'text-n-ruby-11',
  banida: 'text-n-ruby-11',
  numero_errado: 'text-n-ruby-11',
};

const mesAtual = () => {
  const hoje = new Date();
  return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`;
};
const mes = ref(mesAtual());
const carregando = ref(true);
const erro = ref('');
const dados = ref(null);

const carrega = async () => {
  carregando.value = true;
  try {
    const { data } = await api.value.usoWhatsapp({ mes: mes.value });
    dados.value = data;
    erro.value = '';
  } catch (error) {
    erro.value = erroDaApi(error, t('NEUXS_NEXUS.USO.ERRO'));
  } finally {
    carregando.value = false;
  }
};
watch([() => props.accountId, mes], carrega, { immediate: true });

const dolar = valor =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'USD' }).format(
    valor || 0
  );
const numero = valor => new Intl.NumberFormat('pt-BR').format(valor || 0);
const quando = iso =>
  new Date(iso).toLocaleString('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  });

const soma = campo =>
  (dados.value?.caixas || []).reduce((total, c) => total + (c[campo] || 0), 0);
const custo = computed(() => soma('custo_usd'));
const economia = computed(() => soma('economia_usd'));

const totalDoDia = dia => TIPOS.reduce((total, tipo) => total + dia[tipo], 0);
const maiorDia = computed(() =>
  Math.max(1, ...(dados.value?.diario || []).map(totalDoDia))
);
const semEnvios = computed(() =>
  (dados.value?.diario || []).every(dia => totalDoDia(dia) === 0)
);
const altura = (dia, tipo) => `${(dia[tipo] / maiorDia.value) * 100}%`;
const tituloDoDia = dia =>
  [
    new Date(`${dia.dia}T12:00:00`).toLocaleDateString('pt-BR'),
    ...TIPOS.map(
      tipo => `${t(`NEUXS_NEXUS.USO.${ROTULO[tipo]}`)}: ${dia[tipo]}`
    ),
  ].join('\n');

const tipoDaCaixa = caixa => t(`NEUXS_NEXUS.WHATSAPP.TIPOS.${caixa.tipo}`);
const cota = caixa =>
  `${Math.min(100, (caixa.cota_do_mes.usadas / dados.value.gratis_por_mes) * 100)}%`;
const motivos = caixa =>
  Object.entries(caixa.motivos || {}).sort((a, b) => b[1] - a[1]);
const quedas = caixa => dados.value?.eventos?.quedas?.[caixa.id] || 0;
</script>

<template>
  <div class="flex flex-col gap-4">
    <p class="text-sm text-n-slate-11">{{ t('NEUXS_NEXUS.USO.EXPLICA') }}</p>

    <label class="flex items-center gap-2 text-sm text-n-slate-11">
      {{ t('NEUXS_NEXUS.USO.MES') }}
      <input v-model="mes" type="month" class="!mb-0 !w-auto" />
    </label>

    <div v-if="carregando && !dados" class="flex justify-center py-10">
      <Spinner />
    </div>
    <p
      v-else-if="erro"
      class="p-4 text-sm rounded-xl bg-n-ruby-2 text-n-ruby-11"
    >
      {{ erro }}
    </p>
    <p
      v-else-if="!dados.caixas.length"
      class="p-4 text-sm rounded-xl bg-n-alpha-2 text-n-slate-11"
    >
      {{ t('NEUXS_NEXUS.USO.SEM_CAIXAS') }}
    </p>
    <template v-else>
      <!-- Totais -->
      <div class="grid grid-cols-2 gap-3 md:grid-cols-4">
        <div
          v-for="tipo in TIPOS"
          :key="tipo"
          class="flex flex-col gap-1 p-3 border rounded-xl border-n-weak"
        >
          <span class="flex items-center gap-1.5 text-xs text-n-slate-11">
            <span class="rounded-sm size-2.5" :class="COR[tipo]" />
            {{ t(`NEUXS_NEXUS.USO.${ROTULO[tipo]}`) }}
          </span>
          <span class="text-xl font-medium text-n-slate-12">
            {{ numero(dados.totais[tipo]) }}
          </span>
          <span class="text-xs text-n-slate-10">
            {{ t(`NEUXS_NEXUS.USO.${ROTULO[tipo]}_DICA`) }}
          </span>
        </div>
      </div>
      <div class="grid gap-3 md:grid-cols-2">
        <div class="flex flex-col gap-1 p-3 rounded-xl bg-n-amber-2">
          <span class="text-xs text-n-amber-11">
            {{ t('NEUXS_NEXUS.USO.CUSTO') }}
          </span>
          <span class="text-xl font-medium text-n-amber-12">
            {{ dolar(custo) }}
          </span>
        </div>
        <div class="flex flex-col gap-1 p-3 rounded-xl bg-n-teal-2">
          <span class="text-xs text-n-teal-11">
            {{ t('NEUXS_NEXUS.USO.ECONOMIA') }}
          </span>
          <span class="text-xl font-medium text-n-teal-12">
            {{ dolar(economia) }}
          </span>
        </div>
      </div>
      <p class="text-xs text-n-slate-10">
        {{
          t('NEUXS_NEXUS.USO.ESTIMATIVA', {
            preco: dados.preco_usd.toLocaleString('pt-BR', {
              maximumFractionDigits: 4,
            }),
          })
        }}
        ·
        {{
          t('NEUXS_NEXUS.USO.RECEBIDAS', { n: numero(dados.totais.recebidas) })
        }}
      </p>

      <!-- Por dia -->
      <section class="flex flex-col gap-2 p-4 border rounded-xl border-n-weak">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('NEUXS_NEXUS.USO.DIARIO') }}
        </span>
        <p v-if="semEnvios" class="text-sm text-n-slate-11">
          {{ t('NEUXS_NEXUS.USO.SEM_ENVIOS') }}
        </p>
        <div v-else class="flex items-end h-40 gap-px">
          <div
            v-for="dia in dados.diario"
            :key="dia.dia"
            class="flex flex-col-reverse flex-1 h-full min-w-0"
            :title="tituloDoDia(dia)"
          >
            <div
              v-for="tipo in TIPOS"
              :key="tipo"
              :class="COR[tipo]"
              :style="{ height: altura(dia, tipo) }"
            />
          </div>
        </div>
        <div
          v-if="!semEnvios"
          class="flex justify-between text-xs text-n-slate-10"
        >
          <span>{{
            new Date(`${dados.de}T12:00:00`).toLocaleDateString('pt-BR')
          }}</span>
          <span>{{
            new Date(`${dados.ate}T12:00:00`).toLocaleDateString('pt-BR')
          }}</span>
        </div>
      </section>

      <!-- Por caixa -->
      <ul class="flex flex-col gap-2">
        <li
          v-for="caixa in dados.caixas"
          :key="caixa.id"
          class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak"
        >
          <div class="flex flex-wrap items-center justify-between gap-2">
            <div class="flex flex-wrap items-center gap-2">
              <span class="font-medium text-n-slate-12">{{ caixa.nome }}</span>
              <span
                class="px-2 py-0.5 text-xs rounded-md bg-n-alpha-2 text-n-slate-11"
              >
                {{ tipoDaCaixa(caixa) }}
              </span>
              <span
                v-if="caixa.sessao"
                class="text-xs"
                :class="COR_DO_ESTADO[caixa.sessao.estado] || 'text-n-slate-11'"
              >
                {{ t(`NEUXS_NEXUS.WHATSAPP.ESTADOS.${caixa.sessao.estado}`) }}
              </span>
            </div>
            <span class="text-sm text-n-slate-11">{{ caixa.telefone }}</span>
          </div>

          <div class="grid grid-cols-2 gap-2 text-sm md:grid-cols-4">
            <div v-for="tipo in TIPOS" :key="tipo" class="flex flex-col">
              <span class="flex items-center gap-1.5 text-xs text-n-slate-11">
                <span class="rounded-sm size-2" :class="COR[tipo]" />
                {{ t(`NEUXS_NEXUS.USO.${ROTULO[tipo]}`) }}
              </span>
              <span class="text-n-slate-12">{{ numero(caixa[tipo]) }}</span>
            </div>
          </div>

          <div
            v-if="caixa.tipo !== 'oficial'"
            class="grid gap-1 text-sm md:grid-cols-2 text-n-slate-11"
          >
            <span>
              <strong class="font-medium text-n-slate-12">
                {{ t('NEUXS_NEXUS.USO.IA') }}:
              </strong>
              {{ t('NEUXS_NEXUS.USO.LADOS', caixa.ia) }}
            </span>
            <span>
              <strong class="font-medium text-n-slate-12">
                {{ t('NEUXS_NEXUS.USO.ATENDENTES') }}:
              </strong>
              {{ t('NEUXS_NEXUS.USO.LADOS', caixa.atendentes) }}
            </span>
          </div>

          <div v-if="motivos(caixa).length" class="flex flex-col gap-1">
            <span class="text-xs text-n-slate-11">
              {{ t('NEUXS_NEXUS.USO.PORQUE_OFICIAL') }}
            </span>
            <div class="flex flex-wrap gap-1.5">
              <span
                v-for="[motivo, n] in motivos(caixa)"
                :key="motivo"
                class="px-2 py-0.5 text-xs rounded-md bg-n-amber-3 text-n-amber-11"
              >
                {{ t(`NEUXS_NEXUS.USO.MOTIVOS.${motivo}`) }}: {{ numero(n) }}
              </span>
            </div>
          </div>

          <div v-if="caixa.tipo !== 'nao_oficial'" class="flex flex-col gap-1">
            <span class="text-xs text-n-slate-11">
              {{
                t('NEUXS_NEXUS.USO.COTA', {
                  usadas: numero(caixa.cota_do_mes.usadas),
                  total: numero(dados.gratis_por_mes),
                })
              }}
            </span>
            <div class="h-1.5 overflow-hidden rounded-full bg-n-alpha-2">
              <div
                class="h-full bg-n-amber-9"
                :style="{ width: cota(caixa) }"
              />
            </div>
          </div>

          <div class="flex flex-wrap gap-x-4 gap-y-1 text-xs text-n-slate-11">
            <span
              >{{ t('NEUXS_NEXUS.USO.CUSTO') }}:
              {{ dolar(caixa.custo_usd) }}</span
            >
            <span v-if="caixa.economia_usd">
              {{ t('NEUXS_NEXUS.USO.ECONOMIA') }}:
              {{ dolar(caixa.economia_usd) }}
            </span>
            <span v-if="caixa.falhas" class="text-n-ruby-11">
              {{ t('NEUXS_NEXUS.USO.FALHAS', { n: numero(caixa.falhas) }) }}
            </span>
            <span v-if="quedas(caixa)" class="text-n-ruby-11">
              {{ t('NEUXS_NEXUS.USO.QUEDAS', { n: quedas(caixa) }) }}
            </span>
          </div>
        </li>
      </ul>

      <!-- Histórico do aparelho -->
      <section
        v-if="dados.caixas.some(caixa => caixa.sessao)"
        class="flex flex-col gap-2 p-4 border rounded-xl border-n-weak"
      >
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('NEUXS_NEXUS.USO.HISTORICO') }}
        </span>
        <p v-if="!dados.eventos.lista.length" class="text-sm text-n-slate-11">
          {{ t('NEUXS_NEXUS.USO.SEM_EVENTOS') }}
        </p>
        <ul v-else class="flex flex-col gap-1 text-sm">
          <li
            v-for="(evento, i) in dados.eventos.lista"
            :key="i"
            class="flex flex-wrap gap-x-2 text-n-slate-11"
          >
            <span class="text-n-slate-10">{{ quando(evento.em) }}</span>
            <span class="text-n-slate-12">{{ evento.caixa }}</span>
            <span :class="COR_DO_ESTADO[evento.estado]">
              {{ t(`NEUXS_NEXUS.WHATSAPP.ESTADOS.${evento.estado}`) }}
            </span>
            <span v-if="evento.motivo">· {{ evento.motivo }}</span>
          </li>
        </ul>
      </section>
    </template>
  </div>
</template>
